import { useState, useEffect } from 'react'
import api from '../services/api'

// Tabela de Categoria CNH (TCLASSE, frota.classe) — lista do campo "Categ. Habilitação" do funcionário

interface Categoria { clacod: string; clades: string; funcionarios?: number }

function Lista({ lista, onAdicionar, onEditar, onExcluir }: {
  lista: Categoria[]
  onAdicionar: () => void
  onEditar: (c: Categoria) => void
  onExcluir: (c: Categoria) => void
}) {
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Categorias cadastradas ({lista.length})</h3>
        <button className="btn btn-primary" onClick={onAdicionar}>+ Adicionar</button>
      </div>

      {lista.length === 0 && <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Nenhuma categoria cadastrada.</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {lista.map(c => (
          <div key={c.clacod} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{c.clacod} - {c.clades || <span style={{ color: '#9ca3af', fontWeight: 400 }}>(sem descrição)</span>}</div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Funcionários: <b>{c.funcionarios ?? 0}</b></div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button className="btn btn-outline btn-sm" onClick={() => onEditar(c)}>Editar</button>
              <button className="btn btn-outline btn-sm" style={{ color: '#b91c1c' }} onClick={() => onExcluir(c)}
                disabled={(c.funcionarios ?? 0) > 0} title={(c.funcionarios ?? 0) > 0 ? 'Categoria usada em funcionários' : undefined}>Excluir</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function Formulario({ editando, inicial, onVoltar, onSalvo }: {
  editando: boolean
  inicial: Categoria
  onVoltar: () => void
  onSalvo: () => void
}) {
  const [form, setForm] = useState<Categoria>(inicial)
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function salvar() {
    if (!form.clacod.trim()) { setErro('Codigo Classe Invalido'); return }
    setSalvando(true); setErro('')
    try {
      if (editando) await api.put(`/categorias-cnh/${encodeURIComponent(form.clacod)}`, form)
      else await api.post('/categorias-cnh', form)
      onSalvo()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao salvar.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>{editando ? 'Editar Categoria' : 'Nova Categoria'}</h3>
        <button className="btn btn-outline" onClick={onVoltar}>Voltar</button>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field">
          <label>Categoria *</label>
          <input value={form.clacod} maxLength={3} disabled={editando} style={{ width: '6ch' }}
            onChange={e => setForm(f => ({ ...f, clacod: e.target.value.toUpperCase() }))} />
        </div>
        <div className="field">
          <label>Descrição</label>
          <input value={form.clades} maxLength={20} style={{ width: '22ch' }}
            onChange={e => setForm(f => ({ ...f, clades: e.target.value.toUpperCase() }))} />
        </div>
      </div>

      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.75rem' }}>{erro}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
        <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Adicionar'}
        </button>
        <button className="btn btn-outline" onClick={onVoltar}>Cancelar</button>
      </div>
    </div>
  )
}

export default function CategoriasCnh() {
  const [lista, setLista] = useState<Categoria[]>([])
  const [carregado, setCarregado] = useState(false)
  const [atual, setAtual] = useState<Categoria | null>(null)
  const [editando, setEditando] = useState(false)
  const [erro, setErro] = useState('')

  async function carregar() {
    const r = await api.get('/categorias-cnh')
    setLista(r.data)
    setCarregado(true)
  }

  useEffect(() => { carregar().catch(() => setErro('Erro ao carregar as categorias.')) }, [])

  async function excluir(c: Categoria) {
    if (!window.confirm(`Excluir a categoria "${c.clacod}"?`)) return
    setErro('')
    try {
      await api.delete(`/categorias-cnh/${encodeURIComponent(c.clacod)}`)
      await carregar()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao excluir.')
    }
  }

  return (
    <div style={{ maxWidth: 640 }}>
      <h2 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        Categorias de CNH
        <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 400 }} title="Tabela frota.classe">TCLASSE</span>
      </h2>
      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{erro}</div>}

      {!carregado ? null : atual ? (
        <Formulario key={atual.clacod || 'nova'} editando={editando} inicial={atual}
          onVoltar={() => setAtual(null)} onSalvo={async () => { await carregar(); setAtual(null) }} />
      ) : (
        <Lista lista={lista}
          onAdicionar={() => { setAtual({ clacod: '', clades: '' }); setEditando(false) }}
          onEditar={c => { setAtual({ ...c }); setEditando(true) }}
          onExcluir={excluir} />
      )}
    </div>
  )
}
